import {
  PURPOSE,
  USER_ROLE,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { NAVBAR_SECTION_TITLES } from 'src/navbar/Navbar.constants'
import {
  AiGrcQaAbsoluteRoutes,
  AiSoc2ReportSummaryAbsoluteRoutes,
  AuditBotAbsoluteRoutes,
  DirectRoutes,
  ExamineAndTestAbsoluteRoutes,
  ReportScanAbsoluteRoutes,
  RoadmapAbsoluteRoutes,
  SecurityQuestionnaireAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  TrustPortalConfigurationAbsoluteRoutes,
  GrcDashboardsAbsoluteRoutes,
  ScopeAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  EvidenceAbsoluteRoutes,
  AccessManagementAbsoluteRoutes,
  RisksAbsoluteRoutes,
  VendorsAbsoluteRoutes,
  ReportsAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'

export enum PERMISSIONS {
  ADMIN_ACTION = 'admin_action', // this could get more granular but it is a step in the right direction
  AUDITOR_ACTION = 'auditor_action',
  READ = 'read',
  EDIT = 'edit',
  EXPORT = 'export',
  ASK_QUESTIONS = 'ask_questions',
}

export const DEFAULT_REQUIRED_PERMISSIONS = [
  PERMISSIONS.READ,
  PERMISSIONS.EDIT,
  PERMISSIONS.EXPORT,
]

export enum ROLES {
  ADMIN = 'admin',
  USER = 'user',
  READONLY_STANDARD = 'readonly_standard',
  READONLY_TRUSTERO_DEMO = 'readonly_trustero_demo',
  AUDITOR = 'auditor',
  AUDITOR_QC = 'auditor_qc',
  TRUSTPORTAL_PUBLISHER = 'trustportal_publisher',
  TRUSTPORTAL_APPROVER = 'trustportal_approver',
}

export type RoleConfig = {
  routeConfig: {
    denyRoutes: string[]
    hideNavSections: (typeof NAVBAR_SECTION_TITLES)[keyof typeof NAVBAR_SECTION_TITLES][]
    ccHideNavSections?: (typeof NAVBAR_SECTION_TITLES)[keyof typeof NAVBAR_SECTION_TITLES][]
    hideNavRoutes: string[]
    explicitAllowRoutes: string[]
    restrictToAuditView: boolean
    trustPortalPublisherView?: boolean
    trustPortalApproverView?: boolean
  }
  bannerConfig: {
    showBanner: boolean
    bannerMessage: string
    linkText?: string
    internalLink?: string
    link?: string
  }
  permissionsConfig: PERMISSIONS[]
}

export const ROLE_CONFIG = <Record<ROLES, RoleConfig>>{
  [ROLES.ADMIN]: {
    routeConfig: {
      denyRoutes: [],
      hideNavSections: [],
      hideNavRoutes: [],
      explicitAllowRoutes: [],
      restrictToAuditView: false,
    },
    bannerConfig: {
      showBanner: false,
      bannerMessage: '',
    },
    permissionsConfig: [
      PERMISSIONS.ADMIN_ACTION,
      PERMISSIONS.AUDITOR_ACTION,
      PERMISSIONS.READ,
      PERMISSIONS.EDIT,
      PERMISSIONS.EXPORT,
      PERMISSIONS.ASK_QUESTIONS,
    ],
  },
  [ROLES.USER]: {
    routeConfig: {
      denyRoutes: [],
      hideNavSections: [],
      hideNavRoutes: [],
      explicitAllowRoutes: [],
      restrictToAuditView: false,
    },
    bannerConfig: {
      showBanner: false,
      bannerMessage: '',
    },
    permissionsConfig: [
      PERMISSIONS.AUDITOR_ACTION,
      PERMISSIONS.READ,
      PERMISSIONS.EDIT,
      PERMISSIONS.EXPORT,
      PERMISSIONS.ASK_QUESTIONS,
    ],
  },
  [ROLES.READONLY_STANDARD]: {
    routeConfig: {
      denyRoutes: [],
      hideNavSections: [],
      hideNavRoutes: [],
      explicitAllowRoutes: [],
      restrictToAuditView: false,
    },
    bannerConfig: {
      showBanner: true,
      bannerMessage: 'You have read-only access to this account.',
    },
    permissionsConfig: [PERMISSIONS.READ, PERMISSIONS.EXPORT],
  },
  [ROLES.READONLY_TRUSTERO_DEMO]: {
    routeConfig: {
      denyRoutes: [
        DirectRoutes.ACCOUNT_SETTINGS,
        DirectRoutes.USERS,
        DirectRoutes.SCOPE,
      ],
      hideNavSections: [],
      hideNavRoutes: [
        DirectRoutes.ACCOUNT_SETTINGS,
        DirectRoutes.USERS,
        DirectRoutes.SCOPE,
      ],
      explicitAllowRoutes: [],
      restrictToAuditView: false,
    },
    bannerConfig: {
      showBanner: false,
      bannerMessage: '',
    },
    // NOTE: non-read apis will fail when called. but this is the correct behavior.
    permissionsConfig: [
      PERMISSIONS.AUDITOR_ACTION,
      PERMISSIONS.READ,
      PERMISSIONS.EDIT,
      PERMISSIONS.EXPORT,
      PERMISSIONS.ASK_QUESTIONS,
    ],
  },
  [ROLES.AUDITOR]: {
    routeConfig: {
      denyRoutes: [
        AuditBotAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        AiGrcQaAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
      ],
      hideNavSections: [NAVBAR_SECTION_TITLES.ANALYZE],
      ccHideNavSections: [
        NAVBAR_SECTION_TITLES.COMPLIANCE,
        NAVBAR_SECTION_TITLES.ANALYZE,
        NAVBAR_SECTION_TITLES.RISK_MANAGEMENT,
        NAVBAR_SECTION_TITLES.INTEGRATIONS,
      ],
      hideNavRoutes: [
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        DirectRoutes.ACCOUNT_SETTINGS,
        AuditBotAbsoluteRoutes.ROOT,
        AuditBotAbsoluteRoutes.INDEX,
        ExamineAndTestAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.COMPLIANCE_INDEX_PAGE,
        ExamineAndTestAbsoluteRoutes.INDEX_PAGE,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.INDEX,
        AiGrcQaAbsoluteRoutes.ROOT,
        AiGrcQaAbsoluteRoutes.INDEX,
        ReportScanAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.INDEX,
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.RISK_INDEX,
        DirectRoutes.REPORTS,
      ],
      explicitAllowRoutes: [DirectRoutes.AUDITS, DirectRoutes.ACCOUNTS],
      restrictToAuditView: true,
    },
    bannerConfig: {
      showBanner: true,
      bannerMessage:
        'You have auditor access. Please contact your account administrator if you need additional access.',
    },
    permissionsConfig: [
      PERMISSIONS.AUDITOR_ACTION,
      PERMISSIONS.READ,
      PERMISSIONS.EXPORT,
    ],
  },
  [ROLES.AUDITOR_QC]: {
    routeConfig: {
      denyRoutes: [
        AuditBotAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
      ],
      hideNavSections: [],
      ccHideNavSections: [
        NAVBAR_SECTION_TITLES.COMPLIANCE,
        NAVBAR_SECTION_TITLES.RISK_MANAGEMENT,
        NAVBAR_SECTION_TITLES.INTEGRATIONS,
        NAVBAR_SECTION_TITLES.ANALYZE,
      ],
      hideNavRoutes: [
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        DirectRoutes.ACCOUNT_SETTINGS,
        AuditBotAbsoluteRoutes.ROOT,
        AuditBotAbsoluteRoutes.INDEX,
        ExamineAndTestAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.INDEX,
        ReportScanAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.RISK_INDEX,
        ExamineAndTestAbsoluteRoutes.INDEX,
        ExamineAndTestAbsoluteRoutes.INDEX_PAGE,
        TrusteroIntelligenceAbsoluteRoutes.INDEX,
      ],
      explicitAllowRoutes: [
        DirectRoutes.AUDITS,
        DirectRoutes.ACCOUNTS,
        SecurityQuestionnaireAbsoluteRoutes.INDEX,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.SHOW,
        AiGrcQaAbsoluteRoutes.INDEX,
        AiGrcQaAbsoluteRoutes.ROOT,
        AiGrcQaAbsoluteRoutes.SHOW,
        TrusteroIntelligenceAbsoluteRoutes.INDEX,
        TrusteroIntelligenceAbsoluteRoutes.ROOT,
        TrusteroIntelligenceAbsoluteRoutes.SHOW,
        TrustPortalConfigurationAbsoluteRoutes.ROOT,
      ],
      restrictToAuditView: true,
    },
    bannerConfig: {
      showBanner: true,
      bannerMessage:
        'You have auditor access. Please contact your account administrator if you need additional access.',
    },
    permissionsConfig: [
      PERMISSIONS.AUDITOR_ACTION,
      PERMISSIONS.READ,
      PERMISSIONS.EXPORT,
      PERMISSIONS.ASK_QUESTIONS,
    ],
  },
  [ROLES.TRUSTPORTAL_PUBLISHER]: {
    routeConfig: {
      denyRoutes: [
        AuditBotAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
        DirectRoutes.ACCOUNT_SETTINGS,
        ScopeAbsoluteRoutes.ROOT,
        DirectRoutes.COMPANY_INFO,
        PoliciesAbsoluteRoutes.ROOT,
        ControlsAbsoluteRoutes.ROOT,
        EvidenceAbsoluteRoutes.ROOT,
        DirectRoutes.AUDITS,
        TrusteroIntelligenceAbsoluteRoutes.ROOT,
        DirectRoutes.DASHBOARD,
        DirectRoutes.GRC_DASHBOARDS,
        RisksAbsoluteRoutes.ROOT,
        VendorsAbsoluteRoutes.ROOT,
        DirectRoutes.REPORTS,
      ],
      hideNavSections: [],
      ccHideNavSections: [
        NAVBAR_SECTION_TITLES.RISK_MANAGEMENT,
        NAVBAR_SECTION_TITLES.INTEGRATIONS,
        NAVBAR_SECTION_TITLES.HOME,
        NAVBAR_SECTION_TITLES.SETTINGS,
        NAVBAR_SECTION_TITLES.COMPLIANCE,
        NAVBAR_SECTION_TITLES.ANALYZE,
        NAVBAR_SECTION_TITLES.ANALYTICS,
        NAVBAR_SECTION_TITLES.ORGANIZATION,
      ],
      hideNavRoutes: [
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        DirectRoutes.ACCOUNT_SETTINGS,
        AuditBotAbsoluteRoutes.ROOT,
        AuditBotAbsoluteRoutes.INDEX,
        ExamineAndTestAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.COMPLIANCE_INDEX_PAGE,
        ExamineAndTestAbsoluteRoutes.INDEX_PAGE,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.INDEX,
        ReportScanAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.INDEX,
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.RISK_INDEX,
        GrcDashboardsAbsoluteRoutes.ROOT,
        DirectRoutes.DASHBOARD,
        RisksAbsoluteRoutes.ROOT,
        RisksAbsoluteRoutes.INDEX,
        RisksAbsoluteRoutes.SHOW,
        VendorsAbsoluteRoutes.ROOT,
        VendorsAbsoluteRoutes.INDEX,
        VendorsAbsoluteRoutes.SHOW,
        DirectRoutes.REPORTS,
        ReportsAbsoluteRoutes.INDEX_PAGE,
        ScopeAbsoluteRoutes.ROOT,
        DirectRoutes.COMPANY_INFO,
        PoliciesAbsoluteRoutes.ROOT,
        PoliciesAbsoluteRoutes.INDEX,
        PoliciesAbsoluteRoutes.INDEX_PAGE,
        PoliciesAbsoluteRoutes.SHOW,
        ControlsAbsoluteRoutes.ROOT,
        ControlsAbsoluteRoutes.INDEX,
        ControlsAbsoluteRoutes.INDEX_PAGE,
        ControlsAbsoluteRoutes.SHOW,
        EvidenceAbsoluteRoutes.ROOT,
        EvidenceAbsoluteRoutes.INDEX,
        EvidenceAbsoluteRoutes.INDEX_PAGE,
        EvidenceAbsoluteRoutes.SHOW,
        DirectRoutes.AUDITS,
        TrusteroIntelligenceAbsoluteRoutes.ROOT,
        TrusteroIntelligenceAbsoluteRoutes.INDEX,
        TrusteroIntelligenceAbsoluteRoutes.SHOW,
        AiGrcQaAbsoluteRoutes.ROOT,
        AiGrcQaAbsoluteRoutes.INDEX,
        AiGrcQaAbsoluteRoutes.SHOW,
        AccessManagementAbsoluteRoutes.ROOT,
      ],
      explicitAllowRoutes: [],
      restrictToAuditView: false,
      trustPortalPublisherView: true,
    },
    bannerConfig: {
      showBanner: true,
      bannerMessage:
        'You have Trust Portal Publisher access. Please contact your account administrator if you need additional access.',
    },
    permissionsConfig: [PERMISSIONS.READ, PERMISSIONS.EDIT],
  },
  [ROLES.TRUSTPORTAL_APPROVER]: {
    routeConfig: {
      denyRoutes: [
        AuditBotAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
        DirectRoutes.ACCOUNT_SETTINGS,
        ScopeAbsoluteRoutes.ROOT,
        DirectRoutes.COMPANY_INFO,
        PoliciesAbsoluteRoutes.ROOT,
        ControlsAbsoluteRoutes.ROOT,
        EvidenceAbsoluteRoutes.ROOT,
        DirectRoutes.AUDITS,
        TrusteroIntelligenceAbsoluteRoutes.ROOT,
        DirectRoutes.DASHBOARD,
        DirectRoutes.GRC_DASHBOARDS,
        RisksAbsoluteRoutes.ROOT,
        VendorsAbsoluteRoutes.ROOT,
        DirectRoutes.REPORTS,
      ],
      hideNavSections: [],
      ccHideNavSections: [
        NAVBAR_SECTION_TITLES.RISK_MANAGEMENT,
        NAVBAR_SECTION_TITLES.INTEGRATIONS,
        NAVBAR_SECTION_TITLES.HOME,
        NAVBAR_SECTION_TITLES.SETTINGS,
        NAVBAR_SECTION_TITLES.COMPLIANCE,
        NAVBAR_SECTION_TITLES.ANALYZE,
        NAVBAR_SECTION_TITLES.ANALYTICS,
        NAVBAR_SECTION_TITLES.ORGANIZATION,
      ],
      hideNavRoutes: [
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        DirectRoutes.ACCOUNT_SETTINGS,
        AuditBotAbsoluteRoutes.ROOT,
        AuditBotAbsoluteRoutes.INDEX,
        ExamineAndTestAbsoluteRoutes.ROOT,
        ExamineAndTestAbsoluteRoutes.COMPLIANCE_INDEX_PAGE,
        ExamineAndTestAbsoluteRoutes.INDEX_PAGE,
        SecurityQuestionnaireAbsoluteRoutes.ROOT,
        SecurityQuestionnaireAbsoluteRoutes.INDEX,
        ReportScanAbsoluteRoutes.ROOT,
        ReportScanAbsoluteRoutes.INDEX,
        RoadmapAbsoluteRoutes.ROOT,
        RoadmapAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.ROOT,
        AiSoc2ReportSummaryAbsoluteRoutes.INDEX,
        AiSoc2ReportSummaryAbsoluteRoutes.RISK_INDEX,
        GrcDashboardsAbsoluteRoutes.ROOT,
        DirectRoutes.DASHBOARD,
        RisksAbsoluteRoutes.ROOT,
        RisksAbsoluteRoutes.INDEX,
        RisksAbsoluteRoutes.SHOW,
        VendorsAbsoluteRoutes.ROOT,
        VendorsAbsoluteRoutes.INDEX,
        VendorsAbsoluteRoutes.SHOW,
        DirectRoutes.REPORTS,
        ReportsAbsoluteRoutes.INDEX_PAGE,
        ScopeAbsoluteRoutes.ROOT,
        DirectRoutes.COMPANY_INFO,
        PoliciesAbsoluteRoutes.ROOT,
        PoliciesAbsoluteRoutes.INDEX,
        PoliciesAbsoluteRoutes.INDEX_PAGE,
        PoliciesAbsoluteRoutes.SHOW,
        ControlsAbsoluteRoutes.ROOT,
        ControlsAbsoluteRoutes.INDEX,
        ControlsAbsoluteRoutes.INDEX_PAGE,
        ControlsAbsoluteRoutes.SHOW,
        EvidenceAbsoluteRoutes.ROOT,
        EvidenceAbsoluteRoutes.INDEX,
        EvidenceAbsoluteRoutes.INDEX_PAGE,
        EvidenceAbsoluteRoutes.SHOW,
        DirectRoutes.AUDITS,
        TrusteroIntelligenceAbsoluteRoutes.ROOT,
        TrusteroIntelligenceAbsoluteRoutes.INDEX,
        TrusteroIntelligenceAbsoluteRoutes.SHOW,
        AiGrcQaAbsoluteRoutes.ROOT,
        AiGrcQaAbsoluteRoutes.INDEX,
        AiGrcQaAbsoluteRoutes.SHOW,
        TrustPortalConfigurationAbsoluteRoutes.ROOT,
      ],
      explicitAllowRoutes: [],
      restrictToAuditView: false,
      trustPortalApproverView: true,
    },
    bannerConfig: {
      showBanner: true,
      bannerMessage:
        'You have Trust Portal Approver access. Please contact your account administrator if you need additional access.',
    },
    permissionsConfig: [PERMISSIONS.READ, PERMISSIONS.EDIT],
  },
}

export const getRoleConfig = ({
  userRole,
  accountPurpose,
  hasQc,
  isLoading,
}: {
  userRole: USER_ROLE
  accountPurpose?: PURPOSE
  isLoading: boolean
  hasQc?: boolean // only used for auditor users
}): RoleConfig => {
  switch (userRole) {
    case USER_ROLE.ADMIN:
      return ROLE_CONFIG[ROLES.ADMIN]
    case USER_ROLE.USER:
      return ROLE_CONFIG[ROLES.USER]
    case USER_ROLE.READONLY:
      if (accountPurpose === PURPOSE.READONLY_ACCOUNT) {
        return ROLE_CONFIG[ROLES.READONLY_TRUSTERO_DEMO]
      } else {
        return ROLE_CONFIG[ROLES.READONLY_STANDARD]
      }
    case USER_ROLE.AUDITOR:
      if (hasQc || isLoading) {
        return ROLE_CONFIG[ROLES.AUDITOR_QC]
      } else {
        return ROLE_CONFIG[ROLES.AUDITOR]
      }
    case USER_ROLE.TRUSTPORTAL_PUBLISHER:
      return ROLE_CONFIG[ROLES.TRUSTPORTAL_PUBLISHER]
    case USER_ROLE.TRUSTPORTAL_APPROVER:
      return ROLE_CONFIG[ROLES.TRUSTPORTAL_APPROVER]
    default:
      return ROLE_CONFIG[ROLES.READONLY_STANDARD]
  }
}
